import { useCallback } from 'react';
import { useSettingsModal } from '@/components/SettingsModal/store';
import { usePageExperienceContextOptional } from '@/features/experiences/contexts/PageExperienceContext';
import type { PageExperience } from '@/features/experiences/types';
import { useShareModalStore } from '@/features/permissions/components/ShareModal/store';
import { useAnalytics } from '@/hooks/useAnalytics';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useUpgradeModal } from '../hooks/useUpgradeModal';
import {
  resolveGateRule,
  resolveGateTiers,
  type BooleanFeatureFlag,
  type NumericFeatureFlag,
  type PaywallFeature,
} from './gates';
import { useGateTier } from './useGateTier';

// Hooks can't be called conditionally and `undefined` isn't a valid flag name, so
// rules without a limit or an override still subscribe to something. Both reads are
// discarded unless the rule actually declares the corresponding field — which
// matters for the boolean one, since the test mock reports every boolean flag as
// true and would otherwise throw every gate open.
//
// Which flag stands in is arbitrary; these two name no gate in particular. It does
// have to be a real ConfigCat key, since a synthetic one would log an evaluation
// failure on every gate, and the annotations pin the one constraint that is load
// bearing: the limit read has to be numeric or the allowance comparisons below stop
// typechecking.
const DISCARDED_LIMIT_FLAG: NumericFeatureFlag = 'freeTierViewLimit';
const DISCARDED_OVERRIDE_FLAG: BooleanFeatureFlag =
  'experiencesCustomColorEnabled';

type Options = {
  /**
   * Overrides the experience the gate resolves against. Planner chrome renders
   * against a printable child but must gate on the parent planner, so it passes
   * `useEffectiveExperience()`.
   */
  experience?: PageExperience;
  /**
   * How many of the limited thing already exist. Only read by gates with a
   * `limitFlag`; the gate stays open until this reaches the allowance.
   */
  count?: number;
  /**
   * Holds the gate shut while the caller's own data is still in flight. A count
   * read from a half-loaded list would understate it and let a gated user through.
   */
  isLoading?: boolean;
  /**
   * Extra query params to carry through checkout, for resume targets the launch key
   * alone can't identify — the magic chat passes its chat id so the user returns to
   * the conversation they were interrupted in rather than a fresh one.
   */
  redirectParams?: Record<string, string | number>;
};

type FeatureGate = {
  /**
   * Buying something would unlock this. Use it to decide whether to *offer* an
   * upgrade; never true while entitlement is still loading, so a paying user is
   * never briefly upsold, and never true at an entitled hard cap, where there is
   * nothing left to buy.
   */
  isGated: boolean;
  /**
   * Not known to be permitted — `isGated`, or entitlement still resolving. Use it
   * to decide whether to *lock* UI. Locking on `isGated` alone would leave the
   * control open during the loading window, and nothing downstream re-checks.
   */
  isLocked: boolean;
  /**
   * The feature isn't on offer here — render nothing, don't tease it. Either it can
   * never apply to this org type, or it is the owner's to control and this viewer
   * isn't the owner. A caller that must still show its *effect* (a link that is
   * already public) reads that off the experience, not from here.
   */
  isHidden: boolean;
  isLoading: boolean;
  /** The resolved allowance, or null when the feature isn't count-limited. */
  limit: number | null;
  /**
   * `count` has reached whichever allowance applies. Distinct from `isGated`: an
   * entitled user at the paid cap has reached the limit but has nothing to upgrade
   * to, so the caller should say so rather than sell.
   */
  hasReachedLimit: boolean;
  /**
   * A bigger plan would raise this feature's allowance, whether or not the current
   * one is spent. Use it to advertise a cap before the user hits it; use `isGated`
   * for whether the cap is blocking them right now. Always false for a feature with
   * no allowance, and for anyone already entitled.
   */
  isAllowanceUpgradable: boolean;
  /** Runs `action`, or opens the upgrade modal if the feature is gated. */
  requireAccess: (action: () => void) => void;
  /** Opens the upgrade modal directly, for a CTA that has nothing to run. */
  promptUpgrade: () => void;
};

export const useFeatureGate = (
  feature: PaywallFeature,
  {
    experience: experienceOverride,
    count,
    isLoading: isLoadingInput,
    redirectParams,
  }: Options = {}
): FeatureGate => {
  const context = usePageExperienceContextOptional();
  const experience = experienceOverride ?? context?.experience;

  const { openUpgradeModal } = useUpgradeModal();
  const { trackEvent } = useAnalytics();
  const { close: closeShareModal } = useShareModalStore();
  const { close: closeSettingsModal } = useSettingsModal();

  const rule = resolveGateRule(feature, experience?.type);

  // An ungated feature still resolves a scope so the hook order stays stable;
  // 'user'/'plus' is the cheapest read and the result goes unused.
  const {
    isEntitled,
    orgType,
    isLoading: isLoadingTier,
  } = useGateTier(rule?.scope ?? 'user', rule?.requires ?? 'plus', experience);

  const { value: limitValue, loading: isLoadingLimit } = useFeatureFlag(
    rule?.limitFlag ?? DISCARDED_LIMIT_FLAG
  );

  const { value: overrideValue, loading: isLoadingOverride } = useFeatureFlag(
    rule?.overrideFlag ?? DISCARDED_OVERRIDE_FLAG
  );
  const isOverridden = rule?.overrideFlag != null && overrideValue === true;

  // The feature isn't on offer to this org type, so it must not be locked or teased
  // either. Owner-only restrictions aren't expressed here: a non-owner still sees the
  // control (read-only), which is the call site's decision — see `customSlug`.
  const isHidden = Boolean(
    rule?.hiddenForOrgTypes &&
    orgType &&
    rule.hiddenForOrgTypes.includes(orgType)
  );

  // A flagged allowance wins over a fixed one, so a rule can be moved onto a flag
  // without having to drop its previous constant.
  const freeLimit = rule?.limitFlag != null ? limitValue : rule?.freeLimit;
  const hasAllowance = freeLimit != null;

  const limit = hasAllowance
    ? isEntitled
      ? (rule?.entitledLimit ?? Number.POSITIVE_INFINITY)
      : freeLimit
    : null;

  const isLoading =
    Boolean(isLoadingInput) ||
    (rule != null &&
      (isLoadingTier ||
        (rule.limitFlag != null && isLoadingLimit) ||
        (rule.overrideFlag != null && isLoadingOverride)));

  const currentCount = count ?? 0;
  // A feature that isn't on offer here has no allowance to have reached — without
  // this a withheld invite form would still report the maximum as reached.
  const hasReachedLimit = !isHidden && limit != null && currentCount >= limit;

  // Until the flag resolves, `limitValue` is only the compiled-in default. A
  // configured value lower than that would make the count check wrongly permissive,
  // so treat the allowance as unknown rather than trusting the default.
  const isAllowanceProvisional = rule?.limitFlag != null && isLoadingLimit;

  // Whether the plan is what stands in the way. For a count-limited feature that
  // only becomes true once the free allowance is spent — below it everyone is
  // allowed, which is also why the answer needs no tier and stays stable while the
  // tier loads.
  const isPlanRelevant =
    hasAllowance && !isAllowanceProvisional ? currentCount >= freeLimit : true;

  const isRestricted =
    rule != null && !isHidden && !isOverridden && isPlanRelevant;

  // Locked covers "not known to be permitted", so UI stays shut through the loading
  // window. Gated is the narrower claim that an upgrade would open it — false while
  // loading (don't upsell someone who may already have paid) and false for an
  // entitled user at the paid cap (nothing to sell them).
  const isLocked = isRestricted && (isLoading || !isEntitled);
  const isGated = isRestricted && !isLoading && !isEntitled;

  // A bigger plan would raise the allowance — true from the first slot, not only once
  // the last one is spent. `isGated` deliberately waits for the cap, which is right
  // for "may I do this right now?" but wrong for advertising the ceiling: an owner
  // looking at an empty invite form should learn that two is all they get before they
  // run into it. Entitled users are excluded, so nobody at the paid cap is offered
  // something that wouldn't move, and it needs a higher paid cap to point at.
  const isAllowanceUpgradable =
    rule != null &&
    !isHidden &&
    !isOverridden &&
    !isLoading &&
    !isEntitled &&
    hasAllowance &&
    (rule.entitledLimit ?? Number.POSITIVE_INFINITY) > freeLimit;

  const promptUpgrade = useCallback(() => {
    if (!rule) {
      return;
    }

    // The two funnel events carry different payloads, so they can't be collapsed
    // into one call. Both name the `feature`, which is what makes one gate
    // distinguishable from another: `launch` can't, since it is a resume key
    // several gates share and most don't set at all.
    if (rule.analyticsEvent === 'entry:click upgrade') {
      trackEvent('entry:click upgrade', { target: 'button', feature });
    } else {
      trackEvent('experience:click upgrade', {
        target: 'button',
        feature,
        launch: rule.launch ?? '',
        experienceType: experience?.type,
        config: experience?.config ?? undefined,
      });
    }

    // `returnTo` names the modal the gate was reached from, so it's also the one
    // to get out of the way — two stacked modals otherwise. UpgradeModal reopens
    // it on dismiss. Gates without a `returnTo` touch nothing, which is what
    // stops the colours gate closing a share modal it was never opened from.
    if (rule.returnTo === 'share') {
      closeShareModal();
    } else if (rule.returnTo === 'settings') {
      closeSettingsModal();
    }

    openUpgradeModal({
      redirect:
        rule.launch || redirectParams
          ? { launch: rule.launch, queryParams: redirectParams }
          : undefined,
      returnTo: rule.returnTo,
      feature,
      heading: rule.heading,
      tiers: resolveGateTiers(rule),
      experienceType: experience?.type,
      config: experience?.config ?? undefined,
    });
  }, [
    rule,
    feature,
    trackEvent,
    openUpgradeModal,
    experience,
    redirectParams,
    closeShareModal,
    closeSettingsModal,
  ]);

  const requireAccess = useCallback(
    (action: () => void) => {
      // Acting on in-flight data is wrong in both directions, so do neither. A feature
      // withheld from this org type must not fire either — `isHidden` drops `isGated`,
      // so without this the action would run for a viewer the rule never offers it to.
      if (isLoading || isHidden) {
        return;
      }

      if (isGated) {
        promptUpgrade();
        return;
      }

      // Entitled but out of allowance. There's nothing to sell, so decline quietly
      // and leave it to the caller to explain the cap.
      if (hasReachedLimit) {
        return;
      }

      action();
    },
    [isLoading, isHidden, isGated, hasReachedLimit, promptUpgrade]
  );

  return {
    isGated,
    isLocked,
    isHidden,
    isLoading,
    limit,
    hasReachedLimit,
    isAllowanceUpgradable,
    requireAccess,
    promptUpgrade,
  };
};
